import {
  detectRuntime,
  parseMcpServersJson,
  previewSlug,
  isLocalMcpUrl,
  DESCRIPTION_MAX,
  NAME_MAX,
  type ParseFlag,
  type ServerTransport,
} from '@littlebird/axon-mcp/contract';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';

import { electronApis } from '@/features/electron-api';
import type { BundledMcpTemplate } from '@/features/integrations/bundled-mcp-templates';
import type {
  McpPermission,
  OnDeviceMcpCatalogEntry,
} from '@/features/integrations/integrations.types';
import {
  useAddMcpServerMutation,
  useMcpServersQuery,
  useReservedSlugsQuery,
  useTestMcpServerMutation,
} from '@/features/settings/sub-settings/settings-local-mcp.query';
import {
  trackCustomMcpServerConfigPasted,
  trackCustomMcpServerConfigRejected,
} from '@/integrations/integrations.utils';
import { fullDiskAccessPermissionQueryOptions } from '@/query/permissions';

import { evaluateRequirements } from './config-requirements';
import {
  findMissingPermissions,
  permissionsBlockingConnect,
  useOnFullDiskAccessGranted,
} from './server-permissions';

import type {
  ConnectionTestState,
  EnvRow,
  HeaderRow,
  Runtime,
  Source,
  Transport,
} from './types';

const rowsToRecord = (
  rows: ReadonlyArray<{ key: string; value: string }>
): Record<string, string> | undefined => {
  const entries = rows
    .filter((row) => row.key.trim() !== '')
    .map((row) => [row.key.trim(), row.value] as const);
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

const withScheme = (url: string) =>
  url.includes('://') ? url : `http://${url}`;

/**
 * Canonical key for a transport so an exact-duplicate config can be detected.
 * Args are positional (order matters); env/headers are unordered.
 */
const transportKey = (transport: ServerTransport): string => {
  const sortedRecord = (record?: Record<string, string>) =>
    record
      ? JSON.stringify(
          Object.entries(record).sort(([leftKey], [rightKey]) =>
            leftKey.localeCompare(rightKey)
          )
        )
      : '';
  if (transport.kind === 'http') {
    return `http|${transport.url}|${sortedRecord(transport.headers)}`;
  }
  const args = JSON.stringify(transport.args ?? []);
  if (transport.kind === 'stdio') {
    return `stdio|${transport.runtime}|${transport.packageSpec}|${args}|${sortedRecord(transport.env)}`;
  }
  if (transport.kind === 'bundled') {
    return `bundled|${transport.serverId}|${args}|${sortedRecord(transport.env)}`;
  }
  return `binary|${transport.command}|${args}|${sortedRecord(transport.env)}`;
};

export function useAddServerForm({
  onSaved,
  initialCatalogEntry,
  initialBundledTemplate,
  initialUrl,
}: {
  onSaved?: () => void;
  initialCatalogEntry?: OnDeviceMcpCatalogEntry;
  initialBundledTemplate?: BundledMcpTemplate;
  initialUrl?: string;
} = {}) {
  const servers = useMcpServersQuery().data ?? [];
  const reservedSlugs = useReservedSlugsQuery().data ?? [];
  const testMutation = useTestMcpServerMutation();
  const addMutation = useAddMcpServerMutation();

  const [transport, setTransport] = useState<Transport>(
    initialBundledTemplate ? 'bundled' : initialUrl ? 'http' : 'stdio'
  );
  const [runtime, setRuntime] = useState<Runtime>(
    initialCatalogEntry?.runtime ?? 'node'
  );
  const [pkg, setPkg] = useState(initialCatalogEntry?.package_spec ?? '');
  const [command, setCommand] = useState('');
  const [args, setArgs] = useState<string[]>(
    initialCatalogEntry?.arg_hints.map(() => '') ?? []
  );
  const [envs, setEnvs] = useState<EnvRow[]>(
    initialCatalogEntry?.env_hints.map((hint) => ({
      key: hint.name,
      value: hint.default ?? '',
      reveal: !hint.secret,
    })) ?? []
  );
  const [url, setUrl] = useState(initialUrl ?? '');
  const [headers, setHeaders] = useState<HeaderRow[]>([]);
  const [name, setName] = useState(
    initialCatalogEntry?.name ?? initialBundledTemplate?.name ?? ''
  );
  const [description, setDescription] = useState(
    initialCatalogEntry?.description ??
      initialBundledTemplate?.description ??
      ''
  );
  const [jsonOpen, setJsonOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [jsonChoices, setJsonChoices] = useState<string[]>([]);
  const [jsonFlags, setJsonFlags] = useState<ParseFlag[]>([]);
  const [test, setTest] = useState<ConnectionTestState>('idle');
  const [testTools, setTestTools] = useState(0);
  const [testServerName, setTestServerName] = useState('');
  const [testError, setTestError] = useState('');
  const [missingPermissions, setMissingPermissions] = useState<McpPermission[]>(
    []
  );
  const [requirementsChecked, setRequirementsChecked] = useState(false);

  const requiredPermissions =
    initialBundledTemplate?.requiredPermissions ??
    initialCatalogEntry?.required_permissions ??
    [];

  const requirements =
    initialCatalogEntry?.requirements ??
    initialBundledTemplate?.requirements ??
    [];
  const unmetRequirements = requirementsChecked
    ? evaluateRequirements(requirements, { args, envs, headers })
    : [];

  const testGeneration = useRef(0);
  const nameWasAutoFilled = useRef(false);

  const resetTest = () => {
    testGeneration.current++;
    setTest('idle');
    setTestTools(0);
    setTestServerName('');
    setTestError('');
    setMissingPermissions([]);
    setDescription('');
    if (nameWasAutoFilled.current) {
      setName('');
      nameWasAutoFilled.current = false;
    }
  };

  const slug = name.trim()
    ? previewSlug(
        name,
        servers.map((server) => server.slug),
        reservedSlugs
      )
    : '';

  const canTest =
    transport === 'bundled'
      ? true
      : transport === 'stdio'
        ? pkg.trim() !== ''
        : transport === 'binary'
          ? command.trim() !== ''
          : url.trim() !== '' && isLocalMcpUrl(url);

  const buildTransport = (): ServerTransport => {
    if (transport === 'bundled' && initialBundledTemplate) {
      if (requirements.length === 0) return initialBundledTemplate.transport;
      const cleanArgs = args.map((a) => a.trim()).filter(Boolean);
      const environment = rowsToRecord(envs);
      return {
        ...initialBundledTemplate.transport,
        ...(cleanArgs.length > 0 && { args: cleanArgs }),
        ...(environment && { env: environment }),
      };
    }
    if (transport === 'http') {
      const headerValues = rowsToRecord(headers);
      return {
        kind: 'http',
        url: withScheme(url.trim()),
        ...(headerValues && { headers: headerValues }),
      };
    }
    const cleanArgs = args.map((a) => a.trim()).filter(Boolean);
    const environment = rowsToRecord(envs);
    const local = {
      ...(cleanArgs.length > 0 && { args: cleanArgs }),
      ...(environment && { env: environment }),
    };
    return transport === 'stdio'
      ? { kind: 'stdio', runtime, packageSpec: pkg.trim(), ...local }
      : { kind: 'binary', command: command.trim(), ...local };
  };

  const transportToAddKey = canTest ? transportKey(buildTransport()) : null;
  const duplicateServer = transportToAddKey
    ? (servers.find(
        (server) => transportKey(server.transport) === transportToAddKey
      ) ?? null)
    : null;

  const source: Source =
    transport === 'http' ? 'url' : transport === 'binary' ? 'program' : runtime;

  const changeSource = (nextSource: Source) => {
    if (nextSource === 'url') setTransport('http');
    else if (nextSource === 'program') setTransport('binary');
    else {
      setTransport('stdio');
      setRuntime(nextSource);
    }
    resetTest();
  };
  const changePkg = (nextPackage: string) => {
    setPkg(nextPackage);
    const detectedRuntime = detectRuntime(nextPackage);
    if (detectedRuntime) setRuntime(detectedRuntime);
    resetTest();
  };
  const changeCommand = (nextCommand: string) => {
    setCommand(nextCommand);
    resetTest();
  };
  const pickCommandFile = () => {
    void electronApis.axonMcp.pickFile().then((file) => {
      if (file) changeCommand(file);
    });
  };
  const changeUrl = (nextUrl: string) => {
    setUrl(nextUrl);
    resetTest();
  };

  const addArg = () => {
    setArgs((currentArgs) => [...currentArgs, '']);
    resetTest();
  };
  const setArg = (index: number, value: string) => {
    setArgs((currentArgs) =>
      currentArgs.map((argument, argumentIndex) =>
        argumentIndex === index ? value : argument
      )
    );
    resetTest();
  };
  const removeArg = (index: number) => {
    setArgs((currentArgs) =>
      currentArgs.filter((_, argumentIndex) => argumentIndex !== index)
    );
    resetTest();
  };
  const pickFolder = (index: number) => {
    void electronApis.axonMcp.pickDirectory().then((dir) => {
      if (dir) setArg(index, dir);
    });
  };

  const addEnv = () => {
    setEnvs((currentEnvs) => [
      ...currentEnvs,
      { key: '', value: '', reveal: false },
    ]);
    resetTest();
  };
  const setEnvField = (
    index: number,
    field: 'key' | 'value',
    value: string
  ) => {
    setEnvs((currentEnvs) =>
      currentEnvs.map((environment, environmentIndex) =>
        environmentIndex === index
          ? { ...environment, [field]: value }
          : environment
      )
    );
    resetTest();
  };
  const toggleEnvReveal = (index: number) => {
    setEnvs((currentEnvs) =>
      currentEnvs.map((environment, environmentIndex) =>
        environmentIndex === index
          ? { ...environment, reveal: !environment.reveal }
          : environment
      )
    );
  };
  const removeEnv = (index: number) => {
    setEnvs((currentEnvs) =>
      currentEnvs.filter((_, environmentIndex) => environmentIndex !== index)
    );
    resetTest();
  };

  const addHeader = () => {
    setHeaders((currentHeaders) => [...currentHeaders, { key: '', value: '' }]);
    resetTest();
  };
  const setHeaderField = (
    index: number,
    field: 'key' | 'value',
    value: string
  ) => {
    setHeaders((currentHeaders) =>
      currentHeaders.map((header, headerIndex) =>
        headerIndex === index ? { ...header, [field]: value } : header
      )
    );
    resetTest();
  };
  const removeHeader = (index: number) => {
    setHeaders((currentHeaders) =>
      currentHeaders.filter((_, headerIndex) => headerIndex !== index)
    );
    resetTest();
  };

  const jsonPreview = useMemo((): { name: string; detail: string } | null => {
    if (jsonText.trim() === '') return null;
    const parsed = parseMcpServersJson(jsonText);
    if (!parsed.ok) return null;
    const t = parsed.transport;
    const detail =
      t.kind === 'http'
        ? 'a local URL'
        : t.kind === 'binary'
          ? 'a program'
          : t.kind === 'stdio'
            ? t.runtime === 'python'
              ? 'a Python package'
              : 'a Node package'
            : 'a bundled server';
    return { name: parsed.name, detail };
  }, [jsonText]);

  const toggleJson = () => {
    setJsonOpen((o) => !o);
    setJsonError('');
    setJsonChoices([]);
  };

  const applyJson = (pick?: string) => {
    const parsed = parseMcpServersJson(jsonText, pick ? { pick } : undefined);
    if (!parsed.ok) {
      if ('choices' in parsed) {
        setJsonChoices(parsed.choices);
        setJsonError('');
      } else {
        setJsonError(parsed.error);
        setJsonChoices([]);
        trackCustomMcpServerConfigRejected();
      }
      return;
    }
    trackCustomMcpServerConfigPasted();
    if (parsed.transport.kind === 'http') {
      setTransport('http');
      setUrl(parsed.transport.url);
      setHeaders(
        Object.entries(parsed.transport.headers ?? {}).map(([key, value]) => ({
          key,
          value,
        }))
      );
    } else {
      setTransport(parsed.transport.kind);
      if (parsed.transport.kind === 'stdio') {
        setRuntime(parsed.transport.runtime);
        setPkg(parsed.transport.packageSpec);
      } else if (parsed.transport.kind === 'binary') {
        setCommand(parsed.transport.command);
      }
      setArgs(parsed.transport.args ?? []);
      setEnvs(
        Object.entries(parsed.transport.env ?? {}).map(([key, value]) => ({
          key,
          value,
          reveal: false,
        }))
      );
    }
    if (!name.trim() || nameWasAutoFilled.current) {
      setName(parsed.name.slice(0, NAME_MAX));
    }
    nameWasAutoFilled.current = false;
    setJsonFlags(parsed.flags);
    setJsonOpen(false);
    setJsonError('');
    setJsonText('');
    setJsonChoices([]);
    resetTest();
  };

  const testConnection = () => {
    setRequirementsChecked(true);
    if (!canTest || test === 'testing') return;
    setTest('testing');
    setTestError('');
    const generation = ++testGeneration.current;
    testMutation.mutate(
      {
        transport: buildTransport(),
        name: name.trim() || undefined,
        templateId: initialCatalogEntry?.id ?? initialBundledTemplate?.id,
      },
      {
        onSuccess: (result) => {
          if (generation !== testGeneration.current) return;
          if (!result.ok) {
            setTest('failure');
            setTestError(result.error ?? 'The server did not respond.');
            return;
          }
          void findMissingPermissions(
            permissionsBlockingConnect(requiredPermissions)
          ).then((missing) => {
            if (generation !== testGeneration.current) return;
            setMissingPermissions(missing);
            setTestTools(result.toolCount ?? 0);
            setTestServerName(result.serverName ?? '');
            setName((currentName) => {
              if (currentName.trim()) return currentName;
              nameWasAutoFilled.current = true;
              return (result.serverName ?? '').slice(0, NAME_MAX);
            });
            setDescription(
              (currentDescription) =>
                currentDescription || (result.description ?? '')
            );
            if (missing.length > 0) {
              setTest('failure');
              setTestError(
                'The server started, but Littlebird is missing a permission it needs.'
              );
              return;
            }
            setTest('success');
          });
        },
        onError: (error) => {
          if (generation !== testGeneration.current) return;
          setTest('failure');
          setTestError(error instanceof Error ? error.message : String(error));
        },
      }
    );
  };

  // Re-test on its own once the user grants the permission in System
  // Settings; the query polls while a permission is missing.
  const fdaQuery = useQuery(
    fullDiskAccessPermissionQueryOptions({
      enabled: missingPermissions.length > 0,
    })
  );
  useOnFullDiskAccessGranted(fdaQuery.data, () => {
    if (missingPermissions.length > 0) testConnection();
  });

  const reset = () => {
    testGeneration.current++;
    nameWasAutoFilled.current = false;
    setTransport('stdio');
    setRuntime('node');
    setPkg('');
    setCommand('');
    setArgs([]);
    setEnvs([]);
    setUrl('');
    setHeaders([]);
    setName('');
    setDescription('');
    setJsonOpen(false);
    setJsonText('');
    setJsonError('');
    setJsonChoices([]);
    setJsonFlags([]);
    setTest('idle');
    setTestTools(0);
    setTestServerName('');
    setTestError('');
    setMissingPermissions([]);
    setRequirementsChecked(false);
  };

  const saving = addMutation.isPending;
  const canSave =
    name.trim() !== '' &&
    test === 'success' &&
    !duplicateServer &&
    !saving &&
    unmetRequirements.length === 0;

  const save = () => {
    if (!canSave) return;
    addMutation.mutate(
      {
        name: name.trim(),
        ...(description.trim() && { description: description.trim() }),
        transport: buildTransport(),
        template: initialCatalogEntry?.id ?? initialBundledTemplate?.id,
        ...(initialBundledTemplate?.setup && {
          setup: initialBundledTemplate.setup,
        }),
        lastConnectionTest: {
          ok: true,
          ...(testServerName && { serverName: testServerName }),
          toolCount: testTools,
          testedAt: new Date().toISOString(),
        },
      },
      {
        onSuccess: () => {
          reset();
          onSaved?.();
        },
      }
    );
  };

  return {
    transport,
    source,
    changeSource,
    pkg,
    changePkg,
    command,
    changeCommand,
    pickCommandFile,
    args,
    argHints: initialCatalogEntry?.arg_hints ?? [],
    envHints: initialCatalogEntry?.env_hints ?? [],
    setupInstructions: initialCatalogEntry?.setup_instructions ?? [],
    deviceNote: initialBundledTemplate?.deviceNote,
    addArg,
    setArg,
    removeArg,
    pickFolder,
    envs,
    addEnv,
    setEnvField,
    toggleEnvReveal,
    removeEnv,
    url,
    changeUrl,
    urlError: transport === 'http' && url.trim() !== '' && !isLocalMcpUrl(url),
    headers,
    addHeader,
    setHeaderField,
    removeHeader,
    name,
    setName: (nextName: string) => {
      nameWasAutoFilled.current = false;
      setName(nextName.slice(0, NAME_MAX));
    },
    slug,
    description,
    setDescription: (nextDescription: string) =>
      setDescription(nextDescription.slice(0, DESCRIPTION_MAX)),
    jsonOpen,
    jsonText,
    setJsonText,
    jsonError,
    jsonChoices,
    jsonFlags,
    jsonPreview,
    toggleJson,
    applyJson,
    test,
    testTools,
    testServerName,
    testError,
    missingPermissions,
    canTest,
    testConnection,
    requirements,
    unmetRequirements,
    duplicateName: duplicateServer?.name ?? null,
    saving,
    canSave,
    save,
  };
}

export type AddServerForm = ReturnType<typeof useAddServerForm>;
